import React, { useCallback, useMemo, useState } from "react";

import {
  InspectableLineChart,
  Series,
  currencyFormatterNull,
} from "../../../../../components/Charts";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { BREAKPOINTS, GRAPH_COLORS } from "../../../../../utils/constants";
import { ErrorState } from "../../../../../components/States";
import { SegmentedControl } from "../../../../../components/SegmentedControl";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { useInvestmentHistory } from "../../hooks";
import { SectionFreshnessNote } from "../SectionFreshnessNote";
import { REFRESH_NOTICE_COPY, RefreshNotice } from "../RefreshNotice";
import { historyXLabelsOf } from "../../../historyLabels";
import {
  HistoryDataTable,
  HistoryPointDetails,
  HistoryTableColumn,
  pointDetailsTextOf,
} from "../../../historyPoint";
import { InvestmentHistoryPoint } from "../../../../../services/investments/types";
import { ChartAreaSkeleton } from "../../styles";
import {
  PerformanceChartProps,
  LegendItem,
  FlowLine,
  LineStyle,
} from "./types";
import {
  PerformanceCard,
  TitleBlock,
  RangeArea,
  Title,
  Subtitle,
  LegendRow,
  LegendItemRow,
  LegendSwatch,
  LegendToggle,
  ChartBody,
  ChartNote,
  ChartNotice,
  EmptyMessage,
  TaxGapNote,
} from "./styles";

const BROKER_VALUE_COLOR = GRAPH_COLORS[3];
const NET_CONTRIBUTIONS_COLOR = GRAPH_COLORS[1];
const CASH_COLOR = GRAPH_COLORS[0];

const SERIES_STYLE: Record<LineStyle, Partial<Series>> = {
  solid: {},
  dashed: { dashed: true },
  dotted: { dotted: true },
};

const NET_CONTRIBUTIONS_LINE: FlowLine = {
  label: "Net contributions",
  color: NET_CONTRIBUTIONS_COLOR,
  style: "dashed",
  valueOf: (point) => point.netContributions,
};

const CASH_LINE: FlowLine = {
  label: "Cash",
  color: CASH_COLOR,
  style: "dotted",
  valueOf: (point) => point.cashBalance,
};

// A manual account has no cash leg, so both lines would be all-null and their legend entries empty.
const hasValues = (points: InvestmentHistoryPoint[], line: FlowLine) =>
  points.some((point) => line.valueOf(point) !== null);

const ERROR_MESSAGE =
  "We couldn't load the performance history for this investment.";

const MOBILE_CHART_HEIGHT = 260;
const DESKTOP_CHART_HEIGHT = 320;

const PerformanceChartComponent: React.FC<PerformanceChartProps> = ({
  id,
  window,
  options,
  onWindowChange,
  onSetPrice,
  generation,
  taxWithheld,
  showsCash = true,
}) => {
  const isMobile = useResponsive(BREAKPOINTS.tabletMin);
  const {
    points,
    range,
    freshness,
    isLoading,
    error,
    refreshFailed,
    isRefreshing,
    reload,
  } = useInvestmentHistory({ id, window, generation });
  const windowLabel =
    options.find((option) => option.value === window)?.label ?? window;

  const [showCash, setShowCash] = useState(false);
  const hasNetContributions = hasValues(points, NET_CONTRIBUTIONS_LINE);
  const hasCash = showsCash && hasValues(points, CASH_LINE);

  const flowLines = useMemo(() => {
    const lines = hasNetContributions ? [NET_CONTRIBUTIONS_LINE] : [];
    return hasCash && showCash ? [...lines, CASH_LINE] : lines;
  }, [hasNetContributions, hasCash, showCash]);

  const chartData = useMemo(() => {
    if (points.length === 0) {
      return {
        series: [] as Series[],
        readoutSeries: [] as Series[],
        labels: [] as string[],
        tableColumns: [] as HistoryTableColumn<InvestmentHistoryPoint>[],
        yMin: 0 as number | undefined,
      };
    }

    const labels = historyXLabelsOf(points, range?.sampling);
    const flowSeries: Series[] = flowLines.map((line) => ({
      data: points.map(line.valueOf),
      // A null here means incomplete funding history, which a bridged segment would paper over.
      connectNulls: false,
      valueFormatter: currencyFormatterNull,
      label: line.label,
      showMark: false,
      color: line.color,
      ...SERIES_STYLE[line.style],
    }));

    const brokerValue: Series = {
      data: points.map((point) => point.brokerValue),
      // Must not bridge its gaps: a straight segment across a blocked month is indistinguishable
      // from real data, which is the exact claim the valuation state exists to withhold.
      connectNulls: false,
      valueFormatter: currencyFormatterNull,
      label: "Broker value",
      showMark: false,
      area: true,
      color: BROKER_VALUE_COLOR,
    };
    // Drawn last so the `area` stays on top, but read out first like the legend.
    const series = [...flowSeries, brokerValue];
    const readoutSeries = [brokerValue, ...flowSeries];

    const tableColumns = flowLines.map(({ label, valueOf }) => ({
      header: label,
      valueOf,
    }));

    // A hard floor at 0 would push a negative cash or net-contribution point off the plot.
    const plotsNegative = series.some((line) =>
      line.data.some((value) => value !== null && value < 0),
    );

    return {
      series,
      readoutSeries,
      labels,
      tableColumns,
      yMin: plotsNegative ? undefined : 0,
    };
  }, [points, range, flowLines]);

  const chartHeight = isMobile ? MOBILE_CHART_HEIGHT : DESKTOP_CHART_HEIGHT;
  const hasData = chartData.labels.length > 0;
  const readoutDates = useMemo(
    () => points.map((point) => formatDate(point.date, DateFormat.DMY_SPACE)),
    [points],
  );
  const readoutFooter = useCallback(
    (dataIndex: number) =>
      points[dataIndex] ? (
        <HistoryPointDetails point={points[dataIndex]} />
      ) : null,
    [points],
  );
  const readoutFooterText = useCallback(
    (dataIndex: number) =>
      points[dataIndex] ? pointDetailsTextOf(points[dataIndex]) : [],
    [points],
  );

  const showsTaxGapNote =
    !error && hasData && hasNetContributions && (taxWithheld ?? 0) > 0;

  const legendItems: LegendItem[] = [
    { label: "Broker value", color: BROKER_VALUE_COLOR, style: "solid" },
    ...(hasNetContributions ? [NET_CONTRIBUTIONS_LINE] : []),
  ];

  // A failed or in-flight read leaves no points, so the empty copy must not speak for either.
  let chartBody: React.ReactNode;
  if (error) {
    chartBody = <ErrorState message={ERROR_MESSAGE} onRetry={reload} />;
  } else if (isLoading) {
    chartBody = (
      <ChartAreaSkeleton
        role="status"
        aria-label="Loading performance history"
      />
    );
  } else if (hasData) {
    chartBody = (
      <>
        <InspectableLineChart
          inspectLabel="Inspect performance by date"
          readoutDates={readoutDates}
          readoutSeries={chartData.readoutSeries}
          readoutFooter={readoutFooter}
          readoutFooterText={readoutFooterText}
          readoutTestId="investment-performance-readout"
          height={chartHeight}
          series={chartData.series}
          xLabels={chartData.labels}
          yMin={chartData.yMin}
          hideLegend
          hideXAxisLine
          hideYAxisLine
          showHorizontalGrid
        />
        <HistoryDataTable
          caption={`Performance, ${windowLabel}`}
          points={points}
          columns={chartData.tableColumns}
        />
      </>
    );
  } else {
    chartBody = (
      <EmptyMessage>
        No performance data yet for the {windowLabel} time frame.
      </EmptyMessage>
    );
  }

  return (
    <PerformanceCard aria-label="Investment performance chart">
      <TitleBlock>
        <Title>Performance</Title>
        <Subtitle>Broker value vs. net contributions</Subtitle>
        <SectionFreshnessNote freshness={freshness} />
      </TitleBlock>

      {!error && (
        <LegendRow aria-label="Chart legend">
          {legendItems.map((item) => (
            <LegendItemRow key={item.label}>
              <LegendSwatch $color={item.color} $style={item.style} />
              {item.label}
            </LegendItemRow>
          ))}
          {hasCash && (
            <LegendToggle
              type="button"
              aria-pressed={showCash}
              aria-label={`${showCash ? "Hide" : "Show"} cash balance`}
              title={`${showCash ? "Hide" : "Show"} cash balance`}
              $active={showCash}
              onClick={() => setShowCash((visible) => !visible)}
            >
              <LegendSwatch $color={CASH_COLOR} $style={CASH_LINE.style} />
              Cash
            </LegendToggle>
          )}
        </LegendRow>
      )}

      {refreshFailed && (
        <ChartNotice>
          <RefreshNotice
            {...REFRESH_NOTICE_COPY.history}
            isRetrying={isRefreshing}
            onRetry={() => void reload()}
          />
        </ChartNotice>
      )}

      <ChartBody>{chartBody}</ChartBody>

      <RangeArea>
        <SegmentedControl
          options={options}
          value={window}
          onChange={onWindowChange}
          ariaLabel="Performance range"
          testId="investment-performance-filter"
        />
      </RangeArea>

      {/* Every gap the value line leaves gets a sentence, or the line just stops and reads as a rendering fault. */}
      <ChartNote points={points} onSetPrice={onSetPrice} />

      {showsTaxGapNote && (
        <TaxGapNote>
          The gap between the lines is after {formatCurrency(taxWithheld ?? 0)}{" "}
          tax withheld at source; net gain adds it back.
        </TaxGapNote>
      )}
    </PerformanceCard>
  );
};

export const PerformanceChart = React.memo(PerformanceChartComponent);
